import { useMemo } from "react";
import { Tooltip, Text, Avatar } from "@medentic/polaris-react";
import { useParams } from "react-router-dom";
import useInstructors from "@features/events/hooks/useInstructors";
import { Instructor } from "@features/events/types";

interface InstructorsAvatarGroupProps {
  instructorIds?: string[];
  maxShow?: number;
  size?: "small" | "medium" | "large";
  hccId?: string; // Optional override, will use from params if not provided
}

export default function InstructorsAvatarGroup({
  instructorIds,
  maxShow = 5,
  size = "small",
  hccId: providedHccId,
}: InstructorsAvatarGroupProps) {
  const { eventOrganizerId } = useParams<{ eventOrganizerId: string }>();
  const hccId = providedHccId || eventOrganizerId;

  // Fetch instructors using the hook
  const { instructors: allInstructors, isLoading } = useInstructors({
    hccId,
    enabled: Boolean(instructorIds && instructorIds.length > 0),
  });

  // Filter instructors based on provided IDs
  const instructors = useMemo<Instructor[]>(() => {
    if (!instructorIds || instructorIds.length === 0 || !allInstructors) {
      return [];
    }
    return instructorIds
      .map((id) => allInstructors.find((instructor) => instructor.id === id))
      .filter((instructor): instructor is NonNullable<typeof instructor> => instructor !== undefined);
  }, [instructorIds, allInstructors]);

  // Don't render anything while loading or if no instructors
  if (isLoading || !instructors || instructors.length === 0) {
    return null;
  }

  const visibleInstructors: Instructor[] = instructors.slice(0, maxShow);
  const remainingCount: number = instructors.length - maxShow;

  // Size mapping for avatars
  const sizeMap = {
    small: { width: "28px", height: "28px", fontSize: "11px" },
    medium: { width: "36px", height: "36px", fontSize: "13px" },
    large: { width: "44px", height: "44px", fontSize: "15px" },
  };

  const avatarSize = sizeMap[size];

  // Size mapping for Polaris Avatar component
  const polarisSize = size === "small" ? "md" : size === "medium" ? "lg" : "xl";

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {visibleInstructors.map((instructor, index) => {
        const initials = (instructor.nameWithPrefix || "")
          .split(" ")
          .slice(0, 2)
          .map((word: string) => word.charAt(0))
          .join("")
          .toUpperCase();

        return (
          <Tooltip key={instructor.id} content={instructor.nameWithPrefix}>
            <div
              style={{
                marginLeft: index > 0 ? "-8px" : "0",
                zIndex: index,
                position: "relative",
              }}
            >
              {instructor.avatar && instructor.avatar.length > 0 ? (
                <img
                  src={instructor.avatar[0].src}
                  srcSet={instructor.avatar
                    .map((img: { src: string; width: number; height: number }) => `${img.src} ${img.width}w`)
                    .join(", ")}
                  sizes={avatarSize.width}
                  alt={instructor.nameWithPrefix}
                  style={{
                    width: avatarSize.width,
                    height: avatarSize.height,
                    borderRadius: "50%",
                    objectFit: "cover",
                    cursor: "default",
                    border: "2px solid white",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    display: "block",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: avatarSize.width,
                    height: avatarSize.height,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "default",
                    border: "2px solid white",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    overflow: "hidden",
                    backgroundColor: "white",
                  }}
                >
                  <Avatar
                    size={polarisSize}
                    customer={false}
                    name={instructor.nameWithPrefix}
                    initials={initials}
                  />
                </div>
              )}
            </div>
          </Tooltip>
        );
      })}

      {remainingCount > 0 && (
        <Tooltip
          content={`+${remainingCount} more instructor${
            remainingCount > 1 ? "s" : ""
          }`}
        >
          <div
            style={{
              marginLeft: "-8px",
              zIndex: visibleInstructors.length,
              position: "relative",
            }}
          >
            <div
              style={{
                width: avatarSize.width,
                height: avatarSize.height,
                borderRadius: "50%",
                backgroundColor: "#E3E3E3",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "default",
                border: "2px solid white",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Text
                as="span"
                variant="bodySm"
                fontWeight="medium"
                tone="subdued"
              >
                +{remainingCount}
              </Text>
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
}

